// The Maintenance settings, in one place for the sidebar menu and the Maintenance overview (US 9367).
// Each section is a sidebar section; `card` groups sections into the overview's area cards.
// `count` is the key in the overview API's counts, `create` the page that adds a new item.

const SECTIONS = [
  {
    label: 'side_nav.general_settings', card: 'general', items: [
      { key: 'partitions', label: 'administration.partitions.title', icon: 'layer-group', to: { name: 'maintenance.partitions' }, qid: 'partitions-link', count: 'partitions',
        createPermission: 'PARTITION_CREATE', partitionModal: true,
        show: (c, can) => can('PARTITION_VIEW') && !c.parent },
      { key: 'users', label: 'maintenance.users.title', icon: 'user-cog', to: { name: 'maintenance.users' }, qid: 'users-owners-link', count: 'users',
        createPermission: 'USER_CREATE', invite: true,
        show: (c, can) => can('USER_VIEW') },
      { key: 'data_objects', label: 'maintenance.data_objects.title', icon: 'clipboard', to: { name: 'maintenance.data_objects' }, qid: 'data-objects-link', count: 'data_objects',
        create: { name: 'maintenance.data_objects.create' }, createPermission: 'DATA_OBJECT_CREATE',
        show: (c, can) => can('DATA_OBJECT_VIEW') },
      { key: 'process_groups', label: 'maintenance.process_groups.title', icon: 'project-diagram', to: { name: 'maintenance.process_groups' }, qid: 'process-groups-link', count: 'process_groups',
        create: { name: 'maintenance.process_groups.create' }, createPermission: 'PROCESS_GROUP_CREATE',
        show: (c, can) => can('PROCESS_GROUP_VIEW') },
      { key: 'organizations', label: 'maintenance.organizations.title', icon: 'sitemap', to: { name: 'maintenance.organizations' }, qid: 'organizations-link', count: 'organizations',
        create: { name: 'maintenance.organizations.create' }, createPermission: 'ORGANIZATION_CREATE',
        show: () => true },
      { key: 'measures', label: 'maintenance.measures.title', icon: 'toolbox', to: { name: 'maintenance.measures' }, qid: 'measures-link', count: 'measures',
        create: { name: 'maintenance.measures.create' }, createPermission: 'MEASURE_CREATE',
        show: (c, can) => can('MEASURE_VIEW') },
    ]
  },
  {
    label: 'side_nav.process_configuration', card: 'process', items: [
      { key: 'legal_basis', label: 'maintenance.legal_basis.title', icon: 'balance-scale', to: { name: 'maintenance.legal_basis' }, qid: 'legal-basis-link', count: 'legal_basis',
        create: { name: 'maintenance.legal_basis.create' }, createPermission: 'LEGAL_BASIS_CREATE',
        show: (c, can) => can('LEGAL_BASIS_VIEW') && c.gdprAdvancedModule },
      { key: 'data_subjects', label: 'maintenance.data_subjects.title', icon: 'users', to: { name: 'maintenance.data_subjects' }, qid: 'data-subjects-link', count: 'data_subjects',
        create: { name: 'maintenance.data_subjects.create' }, createPermission: 'DATA_SUBJECT_CREATE',
        show: (c, can) => can('DATA_SUBJECT_VIEW') && c.gdprModule },
      { key: 'information_obligations', label: 'maintenance.information_obligation.title', icon: 'envelope', to: { name: 'maintenance.information_obligation' }, qid: 'information-obligation-link', count: 'information_obligations',
        create: { name: 'maintenance.information_obligation.create' }, createPermission: 'INFORMATION_OBLIGATION_CREATE',
        show: (c, can) => can('INFORMATION_OBLIGATION_VIEW') && c.gdprAdvancedModule },
      { key: 'retention_rules', label: 'maintenance.retention_rules.title', icon: 'eraser', to: { name: 'maintenance.retention_rules' }, qid: 'retention-rules-link', count: 'retention_rules',
        create: { name: 'maintenance.retention_rules.create' }, createPermission: 'RETENTION_RULE_CREATE',
        show: (c, can) => can('RETENTION_RULE_VIEW') && c.gdprModule },
    ]
  },
  {
    label: 'side_nav.risk_settings', card: 'risk', items: [
      { key: 'frameworks', label: 'maintenance.frameworks.title', icon: 'book', to: { name: 'maintenance.frameworks' }, qid: 'control-framework-link', count: 'frameworks',
        create: { name: 'maintenance.frameworks.create' }, createPermission: 'FRAMEWORK_CREATE',
        show: c => c.riskModule },
      { key: 'risk_categories', label: 'maintenance.risk_categories.title', icon: 'virus', to: { name: 'maintenance.risk_categories' }, qid: 'risk-categories-link', count: 'risk_categories',
        create: { name: 'maintenance.risk_categories.create' }, createPermission: 'RISK_CATEGORY_CREATE',
        show: c => c.riskModule },
      { key: 'threats', label: 'maintenance.threats.title', icon: 'meteor', to: { name: 'maintenance.threats' }, qid: 'threats-link', count: 'threats',
        create: { name: 'maintenance.threats.create' }, createPermission: 'THREAT_CREATE',
        show: c => c.riskModule },
      { key: 'vulnerabilities', label: 'maintenance.vulnerabilities.title', icon: 'link', to: { name: 'maintenance.vulnerabilities' }, qid: 'vulnerabilities-link', count: 'vulnerabilities',
        create: { name: 'maintenance.vulnerabilities.create' }, createPermission: 'VULNERABILITY_CREATE',
        show: c => c.riskModule },
    ]
  },
  {
    // Templates and the report generator share one section, as on the overview's card
    label: 'maintenance_overview.area_titles.assessments', card: 'assessments', items: [
      { key: 'assessments', label: 'maintenance.assessments.title', icon: 'file-alt', to: { name: 'maintenance.assessments' }, qid: 'assessments-link', count: 'assessments',
        create: { name: 'maintenance.assessments.create' }, createPermission: 'ASSESSMENT_CREATE',
        show: c => c.assessmentModule },
      { key: 'incident_categories', label: 'maintenance.incidents.incident_categories.title', icon: 'file-exclamation', to: { name: 'maintenance.incident_categories' }, qid: 'incident-categories-link', count: 'incident_categories',
        show: (c, can) => can('INCIDENT_CATEGORY_VIEW') && c.incidentModule },
      // Reports generates documents; it has nothing to count
      { key: 'reports', label: 'maintenance.reports.title', icon: 'cloud-download-alt', to: { name: 'maintenance.reports' }, qid: 'reports-link',
        show: () => true },
    ]
  },
];

// Area cards on the overview, in order, with their accent colour and icon
export const CARDS = [
  { key: 'general', accent: 'teal', icon: 'cogs' },
  { key: 'process', accent: 'blue', icon: 'balance-scale' },
  { key: 'risk', accent: 'amber', icon: 'shield-alt' },
  { key: 'assessments', accent: 'violet', icon: 'file-alt' },
];

/**
 * Sidebar sections with the items the user may see in the current workspace
 *
 * @param {Object} client The current workspace from the store
 * @param {Function} can Permission name (key of permissionMap) => Boolean
 * @returns {Array}
 */
export function maintenanceSections(client, can) {
  return SECTIONS
    .map(section => Object.assign({}, section, { items: section.items.filter(item => item.show(client, can)) }))
    .filter(section => section.items.length);
}

/**
 * Overview area cards with the items the user may see, and whether they may add new ones
 *
 * @param {Object} client The current workspace from the store
 * @param {Function} can Permission name (key of permissionMap) => Boolean
 * @returns {Array}
 */
export function maintenanceCards(client, can) {
  const sections = maintenanceSections(client, can);
  return CARDS
    .map(card => Object.assign({}, card, {
      items: sections
        .filter(section => section.card === card.key)
        .reduce((items, section) => items.concat(section.items), [])
        .map(item => Object.assign({}, item, { canCreate: !!item.createPermission && can(item.createPermission) }))
    }))
    .filter(card => card.items.length);
}
